import { SentryError } from '@ifixit/sentry';
import { _js } from '@ifixit/localize';
import ValidationFunctions from 'Shared/validation';
import { CookieUtils } from 'Shared/cookies/cookie-utils';

// GLOBAL CONVENIENCE FUNCTIONS ///////////////////////////////////////////////

/**
 * Convenience function to call Element:getElement with the document as the
 * context object.
 */
export { $E };
window.$E = $E;
function $E(selector) {
   return document.getElement(selector);
}

/**
 * Return an object with the current subdomain and domain values.
 */
export { parseDomain };
window.parseDomain = parseDomain;
function parseDomain() {
   let matches = window.location.host.match(/\b([^.]+)\.([^.]+\.[^.]+)$/);
   return {
      subdomain: matches[1],
      domain: matches[2],
   };
}

/**
 * Parses URL for a specific parameter.
 */
export { getURLParameter };
window.getURLParameter = getURLParameter;
function getURLParameter(parameter) {
   return new URI(window.location.href).getData(parameter);
}

/**
 * As close as we can get to a new keyword, `when` makes sure that its first
 * argument is non-empty (or non-false), and then executes its second argument,
 * passing in the first.
 */
export { when };
window.when = when;
function when(obj, fn, fallback) {
   if (!obj || obj.length === 0) {
      return fallback;
   }

   return fn(obj);
}

/**
 * Registers a 'click' event handler on the element with the specified id,
 * if it exists in the DOM.
 */
export { clickSafe };
window.clickSafe = clickSafe;
function clickSafe(id, eventHandler) {
   return when($(id), element => {
      element.addEvent('click', eventHandler);
   });
}

/**
 * Ensures the handler is called when the window load event fires, or
 * immediately if the load event has already fired.
 */
export { onLoad };
window.onLoad = onLoad;
function onLoad(handler) {
   if (document.readyState == 'complete') {
      handler();
   } else {
      window.addEvent('load', handler);
   }
}

/**
 * Call handler before the window unloads, returning its return value to the
 * native event subscription.
 */
function onUnload(handler) {
   window.addEventListener('beforeunload', e => {
      let msg = handler();
      if (msg) {
         // e.returnValue is needed for a few popular browsers
         return (e.returnValue = msg);
      }
   });
}

/**
 * Syntastic sugar around adding a domready even handler since we do it often.
 */
export { onDomReady };
window.onDomReady = onDomReady;
function onDomReady(handler) {
   document.addEvent('domready' /* @suppress domready */, () => {
      setTimeout(handler, 0);
   });
}

/**
 * If a url has a hash referring to a specific element, handler is called
 * with that element when the dom is ready.
 */
export { onHashFragmentElement };
window.onHashFragmentElement = onHashFragmentElement;
function onHashFragmentElement(handler) {
   let callHandlerWithElement = function callHandlerWithElement() {
      if (window.location.hash) {
         // Strip the # from the beginning of the hash
         let id = window.location.hash.slice(1);
         let element = $(id);

         if (element) {
            handler(element);
         }
      }
   };

   onDomReady(callHandlerWithElement);
   window.addEventListener('hashchange', callHandlerWithElement);
}

/**
 * Fire an event globally so that listeners can be setup before any class
 * is defined.
 */
export { fireGlobal };
window.fireGlobal = fireGlobal;
function fireGlobal(eventName) {
   $(window).fireEvent(eventName, Array.convert(arguments).slice(1));
}

/**
 * Listen to an event globally so that listeners can be setup before any class
 * is defined.
 */
export { listenGlobal };
window.listenGlobal = listenGlobal;
function listenGlobal(eventName, handler) {
   $(window).addEvent(eventName, handler);
}

/**
 * A debug helper that wraps every method of an element with a log message
 * that indicates that that method was called. Useful for seeing what sequence
 * of calls are being made on an element from various places. The optional
 * logger argument takes the element, method name, and method arguments as
 * parameters and can do whatever it wants with them.
 */
export { watch };
window.watch = watch;
function watch(el, logger) {
   if (!logger) {
      logger = function (el, prop, args) {
         // eslint-disable-next-line no-console
         console.log(prop); // @suppress console me
      };
   }
   for (let prop in el) {
      if (typeof el[prop] == 'function') {
         (function (prop, func) {
            el[prop] = function () {
               logger(el, prop, arguments);
               return func.apply(el, arguments);
            };
         })(prop, el[prop]);
      }
   }
}

/**
 * An error class to distinguish from a generic Error, which is usually caused
 * by programmer error. The distinction can be made with the 'instanceof'
 * operator:
 *
 *    try {
 *       // something risky
 *    } catch (err) {
 *       if (err instanceof ApplicationError)
 *          // handle it
 *       else
 *          throw err
 *    }
 */
export { ApplicationError };
window.ApplicationError = ApplicationError;
function ApplicationError(msg) {
   this.message = msg;
}

/**
 * Returns true if the user agent matches that of a known mobile device, and
 * false otherwise.
 * Note: This regex is duplicated in Utils.php
 * Note: this is from: http://stackoverflow.com/a/11381730/13216 which is in
 * turn from http://detectmobilebrowsers.com/
 */
// eslint-disable-next-line no-var
export var mobileDetected = (window.mobileDetected = (function () {
   let isMobile = null;
   return function () {
      if (isMobile !== null) {
         return isMobile;
      }
      let UA = navigator.userAgent || navigator.vendor || window.opera,
         smallUA = UA.slice(0, 4),
         UAMatcher =
            /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i,
         smallUAMatcher =
            /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[23]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i;
      return (isMobile = UAMatcher.test(UA) || smallUAMatcher.test(smallUA));
   };
})());

// eslint-disable-next-line no-var
export var tabletDetected = (window.tabletDetected = (function () {
   let isTablet = null;
   return function () {
      if (isTablet !== null) {
         return isTablet;
      }
      let UA = navigator.userAgent || navigator.vendor || window.opera,
         UAMatcher = /android|ipad|playbook|silk/i;
      return (isTablet = UAMatcher.test(UA));
   };
})());

// FUNCTION EXTENSIONS ////////////////////////////////////////////////////////

Function.implement({
   /**
    * Extend functions with 'wrap', which lets us wrap a function g around a
    * function f, giving g full control over whether or not f gets called, and
    * with what arguments:
    *
    *    function f(msg) {alert(msg)};
    *    var g = f.wrap(function(h, msg) {h('From g: ' + msg)});
    *    g('Hello, World!') => "From g: Hello, World!"
    */
   wrap: function (wrapper) {
      let __method = this;
      return function () {
         return wrapper.apply(this, [__method.bind(this)].concat(Array.convert(arguments)));
      };
   },
});

// ELEMENT EXTENSIONS /////////////////////////////////////////////////////////

/**
 * Extend the Element class with our own custom methods.
 */
Element.implement({
   /**
    * Centers the element on the page, based on the viewport and element
    * dimensions as gleaned from Window.getSize() and Element.getSize(),
    * respectively.
    */
   center: function (options) {
      let el = this.getDimensions();
      let win = window.getSize();
      let scroll = options && options.position == 'top' ? { x: 0, y: 0 } : window.getScroll();

      let left = Math.round(scroll.x + (win.x - el.x) / 2);
      let top = Math.round(scroll.y + (win.y - el.y) / 2);

      this.setStyles({
         left: (left < 0 ? 5 : left) + 'px',
         top: (top < 0 ? 5 : top) + 'px',
      });

      return this;
   },

   /**
    * Adds the 'disabled' attribute. If the element is a button (has the
    * buttonLink class) then add our disabled button style.
    */
   disable: function () {
      this.set('disabled', true);

      if (this.hasClass('buttonLink')) {
         this.addClass('buttonLinkDisabled');
      }
      return this;
   },

   /**
    * Removes the 'disabled' attribute if it exists.
    * If the element is a button and has our buttonLinkDisabled style, remove
    * it.
    */
   enable: function () {
      this.removeAttribute('disabled');

      if (this.hasClass('buttonLinkDisabled')) {
         this.removeClass('buttonLinkDisabled');
      }
      return this;
   },

   /**
    * Sets the element's visibility property to the default value, making the
    * element visible. Returns the element for chaining.
    */
   makeVisible: function () {
      this.setStyle('visibility', '');
      return this;
   },

   /**
    * Sets the element's visibility property to 'hidden', making the element
    * invisible, but keeping it in the page flow. Returns the element for
    * chaining.
    */
   makeInvisible: function () {
      this.setStyle('visibility', 'hidden');
      return this;
   },

   /**
    * Returns true if the element is visible on the page, false otherwise.
    * Taken from http://stackoverflow.com/questions/1056357 (best way to find
    * if a dom object is visible or not using mootools) and modified very
    * slightly to negate the logic.
    */
   visible: function () {
      let w = this.offsetWidth,
         h = this.offsetHeight;
      let force = this.tagName === 'TR';
      let hidden =
         w === 0 && h === 0 && !force
            ? true
            : w !== 0 && h !== 0 && !force
              ? false
              : this.getStyle('display') === 'none';
      return !hidden;
   },

   /**
    * Alias for Element.set('html').
    */
   update: function (str) {
      return this.set('html', str);
   },

   /**
    * Sets or toggles the visibility of this element.
    * Calls show() if visibility is true, hide() if it's false
    * and toggles the element if the argument is left out
    */
   toggle: function (visibility) {
      let show = arguments.length > 0 ? visibility : !this.visible();
      return show ? this.show() : this.hide();
   },

   /**
    * Gives focus to and tries to select a form field.
    */
   activate: function () {
      let tag = this.get('tag');
      let type = this.get('type');

      try {
         this.focus();
         if (
            this.select != undefined &&
            (tag == 'textarea' || (tag == 'input' && ['text', 'password'].contains(type)))
         ) {
            this.select();
         }
         // eslint-disable-next-line no-empty
      } catch {}

      return this;
   },

   /**
    * Returns true if the element is 100% within the bounding rectangle of the
    * browser window i.e. the document doesn't need to be scrolled for it to be
    * visible.
    *
    * Note: doesn't take into account css visibility nor nested scrolling with
    * overflow: hidden.
    *
    * Taken from here: http://stackoverflow.com/a/7557433
    */
   isOnScreen: function () {
      let rect = this.getBoundingClientRect();

      return (
         rect.top >= 0 &&
         rect.left >= 0 &&
         rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
         rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
   },

   /**
    * Tries to scroll to the element smoothly.
    */
   jumpTo: function (options) {
      options = options || {};
      let offset = options.offset === undefined ? 100 : options.offset;
      let behavior = options.behavior || 'smooth';

      window.scrollTo({
         top: this.getPosition().y - offset,
         behavior: behavior,
      });
   },

   /**
    * Returns the zIndex of the first element containing this element (or of
    * this element itself) which has a zIndex that isn't 'auto'; if no element
    * in the stack has a zIndex other than 'auto', then returns 'auto'.
    */
   getEffectiveZindex: function () {
      let itr = this,
         zIndex;
      // eslint-disable-next-line no-empty
      while ((zIndex = itr.getStyle('zIndex')) == 'auto' && (itr = itr.getParent())) {}
      return zIndex;
   },

   isFixed: function () {
      let el = this;
      // Note that in the while condition/assignment we extend the offsetParent
      // manually with $ so that IE doesn't choke on getStyle and retrieve.
      do {
         if (
            el.getStyle('position') == 'fixed' ||
            el.retrieve('pinnedByJS') ||
            el.retrieve('fixedPosition')
         ) {
            return true;
         }
      } while ((el = $(el.offsetParent)));
      return false;
   },

   /**
    * Forces the element to retain it's current dimensions
    * even if the contents change.
    *
    * See: unpinDimensions()
    */
   pinDimensions: function () {
      let size = this.getComputedSize();
      let styles = {
         height: size.height,
         width: size.width,
         display: 'block',
      };
      this.setStyles(styles);
   },

   /**
    * Undoes the action of pinDimensions(), allowing an element to be sized
    * by CSS and it's contents.
    */
   unpinDimensions: function () {
      let styles = {
         height: '',
         width: '',
         display: '',
      };
      this.setStyles(styles);
   },
});

String.implement({
   // Turns text into safe html.
   encodeHtml: function () {
      return String(this)
         .replace(/&/g, '&amp;')
         .replace(/"/g, '&quot;')
         .replace(/'/g, '&#39;')
         .replace(/</g, '&lt;')
         .replace(/>/g, '&gt;');
   },

   rtrim: function (toTrim) {
      return this.replace(new RegExp(toTrim + '$', ''), '');
   },
});

// WINDOW EXTENSIONS //////////////////////////////////////////////////////////

window.isFixed = function () {
   return false;
};

// ARRAY EXTENSIONS ///////////////////////////////////////////////////////////

/**
 * Extends Array with our own methods.
 */
Array.implement({
   /**
    * Returns the first item of the array, or undefined if it is empty.
    */
   first: function () {
      return this.length > 0 ? this[0] : undefined;
   },

   /**
    * Returns the last item of the array, or undefined if it is empty.
    */
   last: function () {
      return this.length > 0 ? this.at(-1) : undefined;
   },

   /**
    * Works like addEvent, but adds the event to each element of an array; adds
    * the two parameters to bind, allowing the caller to specify a context
    * and custom argument(s) with which to run the event handler. The custom
    * arguments must be an array if there's more than one, and they will be
    * appended to the first two arguments to the function: the element the
    * event is attached to and the event itself.
    */
   eachAddEvent: function (event, f, context, customArgs) {
      this.each(el => {
         el.addEvent(event, ev => {
            let args = [el, ev].concat(Array.convert(customArgs));
            context = context == undefined ? window : context;
            f.apply(context, args);
         });
      });
   },

   /**
    * Returns a copy of the array, sorted by a property of its elements.
    */
   sortByProperty: function (property) {
      return Array.convert(this).sort((left, right) => {
         let a = left.getProperty(property);
         let b = right.getProperty(property);

         return a < b ? -1 : a > b ? 1 : 0;
      });
   },
});

// CUSTOM EVENTS //////////////////////////////////////////////////////////////

/**
 * A custom event that fires on any mousedown outside of the element the event
 * is attached to. Thanks to:
 *
 * http://appden.com/javascript/fun-with-custom-events-on-elements-in-mootools/
 */
Element.Events.clickoff = {
   base: 'click',
   condition: function (event) {
      // Stop event from bubbling up; note that it's important to use
      // stopPropagation() instead of stop() here, or clicking links inside the
      // element won't work.
      event.stopPropagation();
      return false;
   },
   onAdd: function (fn) {
      this.getDocument().addEvent('click', fn);
   },
   onRemove: function (fn) {
      this.getDocument().removeEvent('click', fn);
   },
};

// REQUEST EXTENSIONS /////////////////////////////////////////////////////////

if (window.Request == undefined) {
   window.Request = {};
}

Request.activeRequests = new Map();

Request.Batch = {
   options: {
      period: 1500,
   },

   hash: new Hash(),
   pinging: false,
   index: 0,

   add: function (req) {
      req.batchId = this.index++;
      this.hash.set(req.batchId, req);
      this.start();
   },

   remove: function (req) {
      if (!this.hash.has(req.batchId)) {
         return false;
      }

      this.hash.erase(req.batchId);
      if (!this.hash.getLength()) {
         this.stop();
      }

      return true;
   },

   ping: function () {
      this.pinging = true;

      let args = [];
      this.hash.each((req, key) => {
         args.push([key, req.funcName, req.args]);
      });

      new Request.AjaxIO('Request.Batch', {
         onSuccess: function (result) {
            result.each(batch => {
               let id = batch[0];
               let response = JSON.parse(batch[1]);

               if (response.AjaxIO.complete) {
                  this.hash.get(id).fireEvent('complete', [response.AjaxIOData]);
                  this.hash.erase(id);
               }
            });

            // Stop repeating if every request has been filled
            if (this.hash.getLength()) {
               this.nextPing = this.ping.delay(this._period, this);
               // exponential backoff.
               this._period *= 1.1;
            } else {
               this.pinging = false;
            }
         }.bind(this),
      }).send(args);
   },

   start: function () {
      // Always reset the period to the base value
      this._period = this.options.period;
      if (!this.pinging) {
         this.ping();
      }
   },

   stop: function () {
      this.pinging = false;
      clearInterval(this.nextPing);
   },
};

/**
 * new Request.AjaxIO(funcName, {
 *    // options...
 * }).send(arg1, arg2, ...);
 */
export default (Request.AjaxIO = new Class({
   Implements: [Options, Events],

   options: {
      onSuccess: function () {},
      timeout: 0,
   },

   initialize: function (funcName, options) {
      this.setOptions(options);
      this.funcName = funcName;

      let self = this;
      let url = this.options.url ? this.options.url : window.location.toString().split('#')[0];

      // Safari is the only browser that returns window.location as a decoded string.
      // This is only a problem for % literals.

      // We don't check the Browser.Engine here because Chrome shares webkit, but doesn't
      // have this same problem. This regex should be safe to use everywhere.
      url = url.replace(/%(?![\dA-Fa-f]{2})/, '%25');

      this.request = new Request({
         url: url,
         onSuccess: this.onSuccess.bind(this),
         onComplete: function () {
            Request.activeRequests.delete(self);
            Request.completedRequests++;
            self.fireEvent('complete');
         },
         onCancel: function () {
            Request.activeRequests.delete(self);
            self.fireEvent('cancel');
            self._reject();
         },
         onFailure: function () {
            self.fireEvent('error');
            self._reject();
         },
      });
   },

   send: function () {
      let _this = this;
      let args = Array.convert(arguments);
      return new Promise((resolve, reject) => {
         _this._resolve = resolve;
         _this._reject = reject;

         let post = {
            AjaxIO_function: _this.funcName,
            AjaxIO_args: JSON.stringify(args),
         };

         Request.activeRequests.set(_this, post);

         _this.request.send(Hash.toQueryString(post));
      });
   },

   ping: function () {
      this.args = Array.convert(arguments);
      Request.Batch.add(this);
      if (this.options.timeout > 0) {
         this.timeout.delay(this.options.timeout, this);
      }
   },

   timeout: function () {
      if (Request.Batch.remove(this)) {
         this.fireEvent('timeout');
      }
   },

   onSuccess: function (text) {
      let response = JSON.parse(text);
      if (response.error) {
         this.fireEvent('onError', [response]);
         this._reject(response.error);
         if (window.console && console.error) {
            console.error(response.error);
         }
      } else {
         if (response.AjaxIOData === undefined) {
            this._reject();
            if (window.console && console.error) {
               console.error('No data in response');
            }
         } else {
            this.fireEvent('onSuccess', [response.AjaxIOData]);
            this._resolve(response.AjaxIOData);
         }
      }
   },
}));
/**
 * A count of currently in-flight requests.
 *
 * Added so we can wait for ajax requests to finish in selenium tests by
 * waiting for this to be 0.
 */

Request.completedRequests = 0;

// For now lets enable this only for development for a while to make sure we
// don't run into any weird unintended behavior
if (!App.isProduction) {
   window.addEventListener('beforeunload', e => {
      let activeAjaxRequest = function () {
         return (
            window.Request !== undefined &&
            window.Request.activeRequests &&
            window.Request.activeRequests.size > 0
         );
      };

      if (activeAjaxRequest()) {
         // Prompt the user asking them if they are sure they want to
         // leave due to unsaved changes.
         e.returnValue = '';

         // Fail during development to prevent flakiness/unexpected
         // behavior if requests are in flight while trying to leave the page.
         let activeAjaxRequests = JSON.stringify([...window.Request.activeRequests.values()]);

         let errorMessage = `
            Active AJAX request(s) in flight:
            ${activeAjaxRequests}
         `;

         throw new SentryError(errorMessage);
      }
   });
}

// ASSET EXTENSIONS ///////////////////////////////////////////////////////////

// eslint-disable-next-line no-var
export var SettingsCookie = (window.SettingsCookie = (function () {
   return {
      get: function (name) {
         return Cookie.read(name);
      },
      set: function (key, value) {
         // ifixit-specific logic taken from CookieUtils::getCookieDomain.
         let baseDomain = window.location.host;
         if (App.which == 'ifixit') {
            baseDomain = parseDomain()['domain'];
         }
         let options = {
            domain: '.' + baseDomain,
            maxAgeInSeconds: 2_592_000, // 30 days
         };
         CookieUtils.set(key, value, options);
      },
   };
})());

/**
 * Works like a Group, but for a collection of Assets, which must have their
 * onload event handlers installed on instantiation and not after the fact.
 * Takes any number of arrays of hrefs (or plain href strings), creates Assets
 * for each href (according to the extension), and provides events for the
 * successful loading of all js, all css, and all files together.
 *
 * Events:
 *
 *    onCssComplete -- fired when all css files have loaded
 *    onJsComplete  -- fired when all js files have loaded
 *    onComplete    -- fired when all css and js files have loaded
 *
 * Warning:
 *
 *    You *must* provide an options object as the last argument to the
 *    constructor, which should have one or more of the events as attributes.
 *    Adding events to the group after the fact is risky, and could result in
 *    missing a load event.
 */
// eslint-disable-next-line no-var
export var AssetGroup = (window.AssetGroup = new Class({
   Implements: [Options, Events],

   options: {
      onCssComplete: function () {},
      onJsComplete: function () {},
      onComplete: function () {},
   },

   initialize: function () {
      this.cssCount = 0;
      this.cssLoaded = false;
      this.jsCount = 0;
      this.jsLoaded = false;

      let args = Array.convert(arguments);
      let options = args.pop();
      this.setOptions(options);

      let scripts = $$('script');
      let links = $$('link[rel="stylesheet"]');

      args.each(function (arg) {
         Array.convert(arg).each(function (file) {
            if (/\.js\b/.test(file)) {
               if (!scripts.some(s => s.src == file)) {
                  this.jsCount += 1;
                  new Asset.javascript(file, {
                     onload: this.loaded.bind(this, 'js'),
                  });
               }
            } else if (/\.css\b/.test(file) && !links.some(l => l.href == file)) {
               // https://mootools.lighthouseapp.com/projects/2706/tickets/186-asset-css-onload-event-does-not-fire-in-ff3-safari-opera
               // Asset.css.onload does not fire for third-party domains, so disable it
               this.cssCount += 1;
               new Asset.css(file, {
                  onload: this.loaded.bind(this, 'css'),
               });
               //this.loaded.delay(100, this, 'css');
            }
         }, this);
      }, this);

      // just in case we ended up not needing to add any new js or css files,
      // we check our lists of assets to see if firing events is in order
      this.fireEvents();
   },

   loaded: function (which) {
      this[which + 'Count'] -= 1;
      this.fireEvents();
   },

   // fires events in a very stateful manner, checking the queues of js and
   // css files to see if they're empty and if we haven't already fired off
   // events
   fireEvents: function () {
      if (this.cssCount <= 0 && !this.cssLoaded) {
         this.fireEvent('onCssComplete');
         this.cssLoaded = true;
      }
      if (this.jsCount <= 0 && !this.jsLoaded) {
         this.fireEvent('onJsComplete');
         this.jsLoaded = true;
      }
      if (this.cssCount <= 0 && this.jsCount <= 0) {
         this.fireEvent('onComplete');
         this.cssCount = this.jsCount = 0;
      }
   },
}));

// FORM CLASS /////////////////////////////////////////////////////////////////

// eslint-disable-next-line no-var
export var FormLibrary = (window.FormLibrary = {
   getFields: function (form) {
      return form.getElements('input,select,textarea,div[contenteditable]');
   },

   getEditableFields: function (form) {
      return this.getFields(form).filter(el => el.type != 'hidden' && !el.disabled);
   },

   getValues: function (form, options) {
      form = $(form);
      if (!form || form.get('tag') != 'form') {
         return;
      }
      let indexRegex = /\[([\w#-]*)]/;
      let obj = {};

      options = Object.merge(
         {
            prefix: '',
            submitDisabled: false,
            ignoreClass: null,
         },
         options
      );

      Array.each(form.elements, el => {
         if (!el.name) {
            return;
         }
         if (el.name.slice(0, Math.max(0, options.prefix.length)) != options.prefix) {
            return;
         }
         if (el.type && (el.type == 'radio' || el.type == 'checkbox') && el.checked == false) {
            return;
         }
         if (el.disabled && el.disabled == true && !options.submitDisabled) {
            return;
         }

         let name = el.name;
         let indices = [];
         let groups = null;
         while ((groups = name.match(indexRegex))) {
            indices.push(groups[1]);
            name = name.replace(indexRegex, '');
         }
         indices.unshift(name);

         let pointer = obj;
         let index;
         let value;
         // eslint-disable-next-line no-var
         for (var i = 0; i < indices.length - 1; i++) {
            // eslint-disable-next-line no-var
            var next;
            index = indices[i];
            if (index === '') {
               next = indices[i + 1] === '' ? [] : {};
               pointer.push(next);
               pointer = next;
            } else {
               if (!pointer[index]) {
                  next = indices[i + 1] === '' ? [] : {};
                  pointer[index] = next;
               }
               pointer = pointer[index];
            }
         }

         // If the element has a class matching the "ignoreClass" class, then
         // the value there is just the default value, and we don't want to use
         // it.
         value = options.ignoreClass && el.hasClass(options.ignoreClass) ? '' : el.value;

         index = indices[i];
         if (index === '') {
            pointer.push(value);
         } else {
            pointer[index] = value;
         }
      });
      return obj;
   },

   getFirstField: function (form) {
      let els = this.getEditableFields(form);
      let withIndices = els.filter(el => {
         let tabIndex = el.getProperty('tabIndex');
         // Items with no specified tabIndex now show a tabIndex of 0, not null.
         return tabIndex !== null && tabIndex >= 1;
      });
      let firstByIndex = withIndices.sortByProperty('tabIndex')[0];

      return firstByIndex ? firstByIndex : els[0];
   },

   focusFirst: function (form) {
      let first = this.getFirstField(form);
      first.focus();
   },

   selectedValue: function (el) {
      el = $(el);
      return el.options[el.selectedIndex].value;
   },

   submit: function (form) {
      form = $(form);

      if (typeof form.onsubmit == 'function' && !form.onsubmit()) {
         return false;
      } else {
         form.submit();
      }
   },

   create: function (obj) {
      let form = new Element('form');
      if (obj.id) {
         form.setProperty('id', obj.id);
      }

      if (obj['class']) {
         form.setProperty('class', obj['class']);
      }

      let formBody = new Element('div', { class: 'formBody' }).inject(form);
      Hash.each(obj.fields || {}, (field, name) => {
         let box = new Element('div', { class: 'formField' }).inject(formBody);
         let id = field.properties ? field.properties.id : false;
         if (field.label) {
            let label = new Element('label');
            label.setProperty('for', id || name);
            label.set('text', field.label);
            label.inject(box);
         }
         let el = null;
         if (field.type == 'text') {
            el = new Element('input', {
               name: name,
               type: 'text',
               value: field.value || '',
            }).setProperties(field.properties || {});
         }
         if (field.type == 'switch') {
            let input = new Element('input', {
               name: name,
               type: 'checkbox',
               value: field.value || false,
               class: 'switch-input',
            }).setProperties(field.properties || {});
            let span = new Element('span', {
               class: 'switch-track',
            });
            el = new Element('label', {
               class: 'switch',
            })
               .adopt(input)
               .adopt(span);
         }
         el.inject(box);
      });
      new Element('div', { class: 'formField' })
         .adopt(
            new Element('button', {
               type: 'submit',
               class: 'button button-action-solid',
               text: obj.submit || 'Submit',
            })
         )
         .inject(formBody);

      return form;
   },
});

export const Validation = (window.Validation = ValidationFunctions);

// UTILS CLASS ////////////////////////////////////////////////////////////////

// eslint-disable-next-line no-var
export var Utils = (window.Utils = (function () {
   return {
      log: function (body) {
         new Request.API_2_0('js_log/debug', {
            method: 'post',
            onSuccess: function (response) {
               return true;
            },
         }).send(body);

         return true;
      },

      /**
       * An empty error handler function to be used as a default; simply swallows
       * application errors, but allows normal errors to propagate.
       */
      ignoreApplicationErrors: function (err) {
         if (!(err instanceof ApplicationError)) {
            throw err;
         }
      },

      /**
       * Watches a window opened with window.open and executes the callback
       * when it's closed by the user
       */
      onChildWindowClose: function (win, callback, timeout) {
         // Default to timeout = 10 minutes
         timeout = timeout || 600_000;
         // eslint-disable-next-line no-var
         var start = Date.now(),
            windowChecker = setInterval(() => {
               if (win && win.closed) {
                  clearInterval(windowChecker);
                  callback(true);
               }

               if (Date.now() - start > timeout) {
                  clearInterval(windowChecker);
                  callback(false);
               }
            }, 250);
      },

      /**
       * Utility for prompting the user to save any unsaved changes before
       * leaving the page.
       */
      UnsavedChanges: {
         /**
          * Add a handler that will be called when the user leaves the page. If
          * the handler returns a message, the browser will prompt the user.
          * Otherwise, the user will leave the page normally.
          */
         addHandler: function (handler) {
            if (!this.$handlers) {
               // Initialize the list of handlers and check them when the user
               // unloads the page.
               this.$handlers = [];
               onUnload(this.getMessage.bind(this));
            }

            this.$handlers.push(handler);
         },

         /**
          * Checks if any of the handlers currently have an unsaved changes
          * message for the user.
          */
         getMessage: function () {
            if (this.$handlers) {
               // Return the first truthy message returned.
               return Array.pick(this.$handlers.map(handler => handler()));
            }
         },
      },

      /**
       * Returns a truthy value if the browser would currently prompt the user
       * when attempting to leave the page.
       */
      hasUnsavedChanges: function () {
         // Check if there are any truthy messages.
         return !!Utils.UnsavedChanges.getMessage();
      },

      /**
       *
       * Helper function to create a DOM structure.
       * Pass it an object like the following:
       * config = {
       *    tag: 'span', // not required, defaults to 'div'
       *    c  : 'list_of css-classes', // not required
       *    // All other properties will be passed straight through to new Element(...)
       *    children : [array of DOM elements or config objects that will be passed to this function]
       * }
       *
       *
       *
       * Add a list of files in Utils/ *.js as properties on the Utils object
       * This allows us to reuse the scripts in isolated use cases liks embeds
       */
      createElements: function createElements(config) {
         if (typeof config === 'string') {
            return document.createTextNode(config);
         }

         // If this is already a DOM element, just return it;
         if (config.tagName) {
            return config;
         }

         let children = config.children,
            tag = config.tag,
            className = config.c;

         if (className) {
            config['class'] = className;
         }

         // Don't want these getting through to the underlying constructor.
         delete config.tag;
         delete config.children;
         delete config.c;

         let el = new Element(tag || 'div', config);

         if (children) {
            Array.each(children, child => {
               el.grab(createElements(child));
            });
         }

         return el;
      },

      /**
       * Returns an interface for throttling a function call
       * (similar to mootools` :pause Event pseudo)
       *
       * fire() can be called as many times as needed, but the
       * callback is called when fire() hasn't been called for `delay`
       * milliseconds.
       *
       * calling fireNow() executes callback() immediately and clears the timer.
       */
      throttler: function (delay, callback) {
         let timer;

         function fireNow() {
            clear();
            callback.apply(this, Array.convert(arguments));
         }

         function fireLater() {
            clear();
            timer = setTimeout(fireNow, delay);
         }

         function clear() {
            clearTimeout(timer);
         }

         return {
            fire: fireLater,
            fireNow: fireNow,
         };
      },

      /**
       * Returns an interface for throttling a function call.
       * The rate is limited to once per `delay` milliseconds.
       * The design ensures that calling `fire()` guarantees that `callback` will
       * be called within `delay` milliseconds.
       *
       * `fire()` can be called as many times as needed, but the
       * callback will only be called once per `delay` milliseconds.
       */
      rateLimiter: function (delay, callback) {
         let paused, shouldFire;

         function fire() {
            if (paused) {
               shouldFire = true;
               return;
            } else {
               callback.apply(this, Array.convert(arguments));
               pause();
            }
         }

         function pause() {
            shouldFire = false;
            clearTimeout(paused);
            paused = setTimeout(() => {
               paused = null;
               if (shouldFire) {
                  fire();
               }
            }, delay);
         }

         return fire;
      },

      /**
       * Creates an alert div and a close button.  Add this to any element to get
       * a nicely styled alert
       *
       * @contents: a string or an element to be placed in the alert.
       */
      createAlert: function (type, contents) {
         return this.createAlertPrompt(type, contents);
      },

      /**
       * Creates and returns an alert div with a prompt and 'Yes / No' links.
       * Both links close the alert and call `callback(yes)` and pass true if
       * Yes was clicked and false if No was clicked.
       *
       * @contents: a string or an element to be placed in the alert.
       */
      createAlertPrompt: function (type, contents, callback) {
         if (type) {
            type = '.status' + type.capitalize() + '.inPage';
         }

         let alertElement = new Element('div.status' + type);
         let yesnoElementType = 'a.yesno.xbuttonLink';
         let button = new Element('i.fa.fa-times.right.close-status', {
            events: {
               click: alertCloser(false),
            },
         });

         alertElement.set('data-text', contents);

         if (typeof contents === 'string') {
            alertElement.set('text', contents);
         } else {
            alertElement.grab(contents);
         }

         if (callback) {
            let YES = new Element(yesnoElementType, {
               text: _js('Yes'),
               events: {
                  click: alertCloser(true),
               },
            });
            let NO = new Element(yesnoElementType, {
               text: _js('No'),
               events: {
                  click: alertCloser(false),
               },
            });

            alertElement.grab(YES);
            alertElement.appendText('/');
            alertElement.grab(NO);
         }

         alertElement.grab(button, 'top');
         return alertElement;

         function alertCloser(confirmed) {
            return function (ev) {
               if (ev) {
                  ev.stop();
               }
               alertElement.dispose();
               if (callback) {
                  callback(confirmed);
               }
            };
         }
      },

      /**
       * Adds an event handler to "From" that fires the same event with the same
       * arguemtns on "To"
       */
      passEvent: function (eventName, from, to) {
         from.addEvent(eventName, function () {
            to.fireEvent(eventName, arguments);
         });
      },

      /**
       * Provides a collection of methods to implement Events.
       *
       * Use it like this to add events to a class:
       *    Object.append(Thing.prototype, Utils.EventsFunctions);
       *
       * ======  Warning!!! ======
       * doing   Object.append(Thing.prototype, Events.prototype)
       *    or   Object.append(Thing.prototype, new Events())
       *
       * Causes a dangerous bug.  Events.prototype contains the '$events'
       * property, so every new instance of it contains the '$events' property.
       * This is a problem when using one of these objects as a prototype,
       * this.$events will resolve to the same Thing.prototype.$events property
       * every time.  This means that every instance shares the same collection
       * of event handlers, which is obviously bad.
       *
       * The solution is to re-implement the individual event functions and
       * auto-initialize this.$events if it's missing.
       *
       * These functions were copied from Mootools 1.4.5 then the
       * "if (this.$events) ..." was inserted in each method.
       */
      EventsFunctions: {
         addEvent: function (type, fn, internal) {
            type = removeOn(type);
            if (!this.$events) {
               this.$events = {};
            }

            /*<1.2compat>*/
            if (fn == $empty) {
               return this;
            }
            /*</1.2compat>*/

            this.$events[type] = (this.$events[type] || []).include(fn);
            if (internal) {
               fn.internal = true;
            }
            return this;
         },

         addEvents: function (events) {
            if (!this.$events) {
               this.$events = {};
            }

            for (let type in events) {
               this.addEvent(type, events[type]);
            }
            return this;
         },

         fireEvent: function (type, args, delay) {
            if (!this.$events) {
               return this;
            }

            type = removeOn(type);
            let events = this.$events[type];
            if (!events) {
               return this;
            }
            args = Array.convert(args);
            events.each(function (fn) {
               if (delay) {
                  fn.delay(delay, this, args);
               } else {
                  fn.apply(this, args);
               }
            }, this);
            return this;
         },

         removeEvent: function (type, fn) {
            if (!this.$events) {
               return this;
            }

            type = removeOn(type);
            let events = this.$events[type];
            if (events && !fn.internal) {
               let index = events.indexOf(fn);
               if (index != -1) {
                  delete events[index];
               }
            }
            return this;
         },

         removeEvents: function (events) {
            if (!this.$events) {
               return this;
            }

            let type;
            if (typeOf(events) == 'object') {
               for (type in events) {
                  this.removeEvent(type, events[type]);
               }
               return this;
            }
            if (events) {
               events = removeOn(events);
            }
            for (type in this.$events) {
               if (events && events != type) {
                  continue;
               }
               let fns = this.$events[type];
               for (let i = fns.length; i--; ) {
                  if (i in fns) {
                     this.removeEvent(type, fns[i]);
                  }
               }
            }
            return this;
         },
      },

      // Returns a promise that resolves after `duration` ms.
      timeoutPromise: function (duration) {
         return new Promise((resolve, reject) => {
            setTimeout(resolve, duration);
         });
      },
   };

   /**
    * Both removeOn and $empty are only used for the above Utils.EventsFunctions
    */
   function removeOn(string) {
      return string.replace(/^on([A-Z])/, (full, first) => first.toLowerCase());
   }
   function $empty() {}
})());

/*
HTMLEncode - Encode HTML special characters.
Copyright (c) 2006 Thomas Peri, http://www.tumuski.com/

Permission is hereby granted, free of charge, to any person obtaining a
copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The Software shall be used for Good, not Evil.

The above copyright notice and this permission notice shall be included
in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

/**
 * HTML-Encode the supplied input
 *
 * Parameters:
 *
 * (String)  source    The text to be encoded.
 *
 * (boolean) display   The output is intended for display.
 *
 *                     If true:
 *                     * Tabs will be expanded to the number of spaces
 *                       indicated by the 'tabs' argument.
 *                     * Line breaks will be converted to <br />.
 *
 *                     If false:
 *                     * Tabs and linebreaks get turned into &#____;
 *                       entities just like all other control characters.
 *
 * (integer) tabs      The number of spaces to expand tabs to.  (Ignored
 *                     when the 'display' parameter evaluates to false.)
 *
 * v 0.3 - January 4, 2006
 */
export { htmlEncode };
window.htmlEncode = htmlEncode;
function htmlEncode(source, display, tabs) {
   function special(source) {
      let result = '';
      for (let i = 0; i < source.length; i++) {
         let c = source.charAt(i);
         if (c < ' ' || c > '~') {
            c = '&#' + c.charCodeAt() + ';';
         }
         result += c;
      }
      return result;
   }

   function format(source) {
      // Use only integer part of tabs, and default to 4
      tabs = tabs >= 0 ? Math.floor(tabs) : 4;

      // split along line breaks
      let lines = source.split(/\r\n|\r|\n/);

      // expand tabs
      for (let i = 0; i < lines.length; i++) {
         let line = lines[i];
         let newLine = '';
         for (let p = 0; p < line.length; p++) {
            let c = line.charAt(p);
            if (c === '\t') {
               let spaces = tabs - (newLine.length % tabs);
               for (let s = 0; s < spaces; s++) {
                  newLine += ' ';
               }
            } else {
               newLine += c;
            }
         }
         // If a line starts or ends with a space, it evaporates in html
         // unless it's an nbsp.
         newLine = newLine.replace(/(^ )|( $)/g, '&nbsp;');
         lines[i] = newLine;
      }

      // re-join lines
      let result = lines.join('<br />');

      // break up contiguous blocks of spaces with non-breaking spaces
      result = result.replace(/ {2}/g, ' &nbsp;');

      // tada!
      return result;
   }

   let result = source;

   // ampersands (&)
   result = result.replace(/&/g, '&amp;');

   // less-thans (<)
   result = result.replace(/</g, '&lt;');

   // greater-thans (>)
   result = result.replace(/>/g, '&gt;');

   // eslint-disable-next-line unicorn/prefer-ternary
   if (display) {
      // format for display
      result = format(result);
   } else {
      // Replace quotes if it isn't for display,
      // since it's probably going in an html attribute.
      result = result.replace(new RegExp('"', 'g'), '&quot;');
   }

   // special characters
   result = special(result);

   // tada!
   return result;
}

export const Geo = {
   // This list should match the `getEuCountriesList` list in GeoUtils.php.
   EUCountries: [
      'AT',
      'BE',
      'BG',
      'CY',
      'CZ',
      'DE',
      'DK',
      'EE',
      'ES',
      'FI',
      'FR',
      'GB',
      'GR',
      'HR',
      'HU',
      'IE',
      'IT',
      'LT',
      'LU',
      'LV',
      'MT',
      'NL',
      'PL',
      'PT',
      'RO',
      'SE',
      'SI',
      'SK',
   ],

   /**
    * Calls the provided callback with the user's country code. Retrieves the
    * country code from a Cookie if it exists. If not, it is retrieved from the
    * API and set in a Cookie.
    */
   locate: function (callback) {
      // function to call the callback with the promise's result
      function resultToCallback(result) {
         if (callback) {
            callback(result.cc, result.rc);
         }
         return result;
      }

      if (Geo._location) {
         return Geo._location.then(resultToCallback);
      }

      Geo._location = new Promise(resolve => {
         function done(countryCode, regionCode) {
            resolve({ cc: countryCode, rc: regionCode });
         }

         // Allow easy and simple override for debugging.
         let countryMatch = window.location.search.match(/countryCode=(\w+)/);
         let regionMatch = window.location.search.match(/regionCode=(\w+)/);

         if (countryMatch) {
            CookieUtils.set('countryCodeOverride', countryMatch[1], {
               maxAgeInSeconds: 3600, // 1 hour
            });
         }

         if (countryMatch || regionMatch) {
            if (countryMatch && regionMatch) {
               done(countryMatch[1], regionMatch[1]);
               return;
            } else if (countryMatch) {
               done(countryMatch[1]);
               return;
            } else {
               done(undefined, regionMatch[1]);
               return;
            }
         }

         const countryCodeOverride = Cookie.read('countryCodeOverride');
         if (countryCodeOverride) {
            done(countryCodeOverride);
            return;
         }

         let countryCode = Cookie.read('countryCode');
         let regionCode = Cookie.read('regionCode');

         if (countryCode !== null && regionCode !== null) {
            done(countryCode, regionCode);
            return;
         } else {
            new Request.API_2_0('geo_locate', {
               method: 'get',
               onSuccess: function (response) {
                  countryCode = response.country_code;
                  regionCode = response.region_code;

                  CookieUtils.set('countryCode', countryCode, {
                     maxAgeInSeconds: 432_000, // 5 days
                  });
                  CookieUtils.set('regionCode', regionCode, {
                     maxAgeInSeconds: 432_000, // 5 days
                  });

                  done(countryCode, regionCode);
               },
               onError: done,
            }).send();
         }
      });

      return Geo._location.then(resultToCallback);
   },

   runEverywhereButEU: function (callback) {
      Geo.locate((countryCode, regionCode) => {
         if (countryCode) {
            let cCode = countryCode.toUpperCase();
            let isEUCountry = Geo.EUCountries.includes(cCode);

            if (!isEUCountry) {
               callback();
            }
         }
      });
   },
};

// Our tiny promise library
/**
 * ========================
 * NOTE: DEPRECATED! Please use the standards compliant Promise object.
 * ========================
 *
 * A dead simple promise / futures implementation
 *
 * Caller
 * ======
 *
 * var data = getStuffFromServer();
 * data.getValue(function ready(response) {
 *    // do something with response
 * });
 *
 * function getStuffFromServer() {
 *    var data = new Future();
 *    new Request({
 *       ...
 *       onComplete: function(response) {
 *          data.resolve(response);
 *       }
 *    });
 *    return data;
 * }
 *
 */
export { Future };
window.Future = Future;
function Future() {
   this.callbacks = [];
   this.resolved = false;
}

(function (prototype) {
   /**
    * Resolve this promise with the specified value
    *
    * Effectively "set" the value that will be passed to the getValue callbacks
    */
   prototype.resolve = function (resolution) {
      let self = this;
      if (self.resolved) {
         throw "Can't resolve a promise more than once";
      }

      self.resolution = resolution;
      self.resolved = true;

      self.callbacks.each(callback => {
         callback(resolution, self._error);
      });
      self.callbacks = [];
   };

   /**
    * Resolve this promise with an error.
    *
    * `error` will be passed to each callback like so:
    * callback(value, error)
    */
   prototype.error = function (error) {
      this._error = error;
      this.resolve();
   };

   /**
    * Get the value of this promise when it's ready. The resolved value will
    * be passed to the readyCallback().
    *
    * readyCallback: function(value, error) {}
    *
    * Returns: true if this promise is already resolved and readyCallback
    * was called immediately.
    *
    * Note: error is normally null and is of course, not required
    */
   prototype.getValue = function (readyCallback) {
      if (this.resolved) {
         readyCallback(this.resolution, this._error);
         return true;
      } else {
         this.callbacks.push(readyCallback);
         return false;
      }
   };

   /**
    * Returns true if the promise has been resolved.
    */
   prototype.isResolved = function () {
      return this.resolved;
   };
})(Future.prototype);

/**
 * Add the 'beforePrint' and 'afterPrint' events to window. Note: these may
 * fire multiple times (including before/after) while actually printing.
 *
 * Use them like so:
 * window.addEvent('beforePrint', function() {
 *    ...
 * });
 *
 * Derived from the code seen here:
 * http://tjvantoll.com/2012/06/15/detecting-print-requests-with-javascript/
 */
(function () {
   let inPrintMode = false;
   function beforePrint() {
      // Prevent multiple firings
      if (inPrintMode) {
         return;
      }
      window.fireEvent('beforePrint');
   }
   function afterPrint() {
      inPrintMode = false;
      window.fireEvent('afterPrint');
   }

   if (window.matchMedia) {
      let mediaQueryList = window.matchMedia('print');
      if (mediaQueryList.matches) {
         beforePrint();
      }
      mediaQueryList.addListener(mql => {
         if (mql.matches) {
            beforePrint();
         } else {
            afterPrint();
         }
      });
   }

   window.onbeforeprint = beforePrint;
   window.onafterprint = afterPrint;
})();

export { goToLastPage, getLastPage };
function goToLastPage() {
   window.location = getLastPage();
}

function getLastPage() {
   // If this was opened in a new window (e.g., target="_blank" rel="noreferrer"), then
   // close it and refresh the original window. The assumption is that this
   // behavior is more desirable that redirecting to last viewed page if
   // the query parameter close_after is set.
   if (
      new URI().getData('close_after') === '1' &&
      window.opener &&
      window.opener.location.hostname === window.location.hostname
   ) {
      window.opener.location.reload();
      window.close();
   }

   let loc = SettingsCookie.get('last_page') || '/';

   return decodeURIComponent(loc);
}
